import { useEffect, useState } from "react";
import api, { inr, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import ImageUpload from "../../components/ImageUpload";

const CATS = ["lehengas", "sharara-sets", "sarees", "jackets-cape-sets", "anarkalis", "suits-kurta-sets", "jumpsuits", "tops-shirts", "dresses", "co-ord-sets", "kaftans", "indo-western"];
const OCCS = ["Wedding", "Mehendi", "Sangeet", "Cocktail", "Festive", "Reception", "Sagai", "Vacation"];
const FABRICS = ["Raw Silk", "Organza", "Georgette", "Banarasi Silk", "Velvet", "Chanderi", "Tulle"];
const SIZE_PRESETS = ["XS", "S", "M", "L", "XL", "XXL", "Free Size"];

const EMPTY = {
  name: "", sku: "", category: "lehengas", occasion: "Wedding", occasions: [],
  price: 50000, compare_at_price: "", purchase_price: "",
  fabric: "Raw Silk", color: "Ivory", primary_image: "", hover_image: "", description: "",
  care: "Dry clean only.", delivery: "Ships in 2-3 weeks.", stock: 10,
  is_new: true, is_bestseller: false, on_sale: false, offline_only: false,
  sizes: ["XS", "S", "M", "L", "XL"], colors: ["Ivory"], images: [],
  stock_by_size: {},
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p, compare_at_price: p.compare_at_price || "", purchase_price: p.purchase_price || "" });
    setOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: Number(form.stock),
        compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
        purchase_price: form.purchase_price !== "" && form.purchase_price != null ? Number(form.purchase_price) : null,
        colors: [form.color],
        hover_image: form.hover_image || form.primary_image,
        images: [form.primary_image, form.hover_image || form.primary_image].filter(Boolean),
      };
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Products</h1>
          <p className="text-ink-soft text-sm mt-1">{products.length} pieces in catalogue</p>
        </div>
        <button onClick={openNew} className="btn-ink flex items-center gap-2 px-5 py-3 overline" data-testid="add-product-btn">
          <Plus size={15} /> Add Product
        </button>
      </div>

      <div className="border border-line rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.03] text-left">
            <tr className="overline text-ink-soft">
              <th className="p-4 font-medium">Product</th>
              <th className="p-4 font-medium">SKU</th>
              <th className="p-4 font-medium">Category</th>
              <th className="p-4 font-medium">Price</th>
              <th className="p-4 font-medium">Stock</th>
              <th className="p-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {products.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.primary_image} alt="" className="h-12 w-10 object-cover bg-[#efece6]" />
                    <span className="font-serif text-base">{p.name}</span>
                  </div>
                </td>
                <td className="p-4 font-mono text-xs text-ink-soft">{p.sku || <span className="italic text-red-500">missing</span>}</td>
                <td className="p-4 capitalize text-ink-soft">{p.category}</td>
                <td className="p-4">{inr(p.price)}</td>
                <td className="p-4">{p.stock}</td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(p)} className="p-2 hover:text-gold-deep" data-testid={`edit-${p.slug}`}><Pencil size={15} /></button>
                    <button onClick={() => del(p.id)} className="p-2 hover:text-red-700" data-testid={`delete-${p.slug}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <L label="Name"><Input value={form.name} onChange={set("name")} data-testid="pf-name" /></L>
            <L label="SKU / Barcode value">
              <div className="flex gap-2">
                <Input
                  value={form.sku}
                  onChange={set("sku")}
                  placeholder="e.g. AMB-1-L"
                  data-testid="pf-sku"
                  className="font-mono uppercase"
                />
                <button
                  type="button"
                  onClick={() => {
                    // Next serial-wise available number (fills gaps), suffixed with size.
                    const used = new Set();
                    products.forEach((p) => {
                      if (editing && p.id === editing.id) return;
                      const m = /^AMB-(\d+)/i.exec(p.sku || "");
                      if (m) used.add(parseInt(m[1], 10));
                    });
                    let n = 1;
                    while (used.has(n)) n += 1;
                    const first = (form.sizes && form.sizes[0]) || "";
                    const code = first === "Free Size" ? "FS" : String(first).toUpperCase();
                    setForm((f) => ({ ...f, sku: code ? `AMB-${n}-${code}` : `AMB-${n}` }));
                  }}
                  className="overline px-3 border border-line hover:border-ink whitespace-nowrap text-xs"
                  data-testid="pf-sku-generate"
                >
                  Auto
                </button>
              </div>
              <span className="text-[0.65rem] text-ink-soft mt-1.5 block">
                Used as the barcode value. Print CODE128 labels at <code className="mono">/pos/barcodes</code>. Leave blank to use the auto suggestion.
              </span>
            </L>
            <div className="grid grid-cols-2 gap-4">
              <L label="Category">
                <Input
                  list="pf-category-list"
                  value={form.category}
                  onChange={set("category")}
                  placeholder="Type or pick — e.g. lehengas, custom-bridal…"
                  data-testid="pf-category"
                  className="capitalize"
                />
                <datalist id="pf-category-list">{CATS.map((c) => <option key={c} value={c} />)}</datalist>
              </L>
              <L label="Occasion">
                <Input
                  list="pf-occasion-list"
                  value={form.occasion}
                  onChange={set("occasion")}
                  placeholder="Type or pick — e.g. Wedding, Sangeet, Diwali…"
                  data-testid="pf-occasion"
                />
                <datalist id="pf-occasion-list">{OCCS.map((c) => <option key={c} value={c} />)}</datalist>
              </L>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <L label="Price (₹)"><Input type="number" value={form.price} onChange={set("price")} data-testid="pf-price" /></L>
              <L label="Compare at (₹)"><Input type="number" value={form.compare_at_price} onChange={set("compare_at_price")} /></L>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <L label="Purchase price / Cost (₹)">
                <Input type="number" value={form.purchase_price} onChange={set("purchase_price")} placeholder="What you paid per unit" data-testid="pf-purchase-price" />
              </L>
              <L label="Margin">
                <div className="h-10 flex items-center px-3 text-sm font-mono text-emerald-400" data-testid="pf-margin">
                  {form.purchase_price !== "" && form.purchase_price != null && Number(form.price) > 0
                    ? `${inr(Number(form.price) - Number(form.purchase_price))} (${Math.round(((Number(form.price) - Number(form.purchase_price)) / Number(form.price)) * 100)}%)`
                    : "—"}
                </div>
              </L>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <L label="Fabric">
                <Input
                  list="pf-fabric-list"
                  value={form.fabric}
                  onChange={set("fabric")}
                  placeholder="Type or pick — e.g. Raw Silk, Chanderi, Tissue…"
                  data-testid="pf-fabric"
                />
                <datalist id="pf-fabric-list">{FABRICS.map((c) => <option key={c} value={c} />)}</datalist>
              </L>
              <L label="Total stock (auto if per-size)"><Input type="number" value={form.stock} onChange={set("stock")} data-testid="pf-total-stock" /></L>
            </div>
            <L label="Colour">
              <Input value={form.color} onChange={set("color")} placeholder="e.g. Ivory, Blush, Emerald" data-testid="pf-color" />
            </L>

            {form.sizes && form.sizes.length > 0 && (
              <div className="border-t border-line pt-4">
                <p className="overline text-ink-soft mb-2">Stock per size · leave a size at 0 to mark it sold out</p>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {form.sizes.map((s) => (
                    <label key={s} className="flex flex-col gap-1">
                      <span className="text-xs font-mono text-ink-soft">{s}</span>
                      <Input
                        type="number"
                        min={0}
                        value={form.stock_by_size?.[s] ?? 0}
                        onChange={(e) => {
                          const val = Math.max(0, Number(e.target.value) || 0);
                          const sbs = { ...(form.stock_by_size || {}), [s]: val };
                          // Auto-compute total from sizes
                          const total = Object.values(sbs).reduce((a, b) => a + (Number(b) || 0), 0);
                          setForm({ ...form, stock_by_size: sbs, stock: total });
                        }}
                        data-testid={`pf-stock-${s}`}
                        className="font-mono text-center"
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <ImageUpload
                label="Primary Image"
                value={form.primary_image}
                onChange={(url) => setForm({ ...form, primary_image: url })}
              />
              <ImageUpload
                label="Hover Image (optional)"
                value={form.hover_image}
                onChange={(url) => setForm({ ...form, hover_image: url })}
              />
            </div>
            <label className="block">
              <span className="overline text-ink-soft mb-1.5 block">Description</span>
              <Textarea
                rows={8}
                value={form.description}
                onChange={set("description")}
                data-testid="pf-description"
                placeholder={"Write the product description. You can:\n\n- Use `- ` at the start of a line for bullet points\n- Use `1. ` for numbered lists\n- Press Enter twice for a new paragraph\n- Wrap **word** in double stars for bold, *word* for italic"}
                className="font-sans"
              />
              <span className="text-[0.65rem] text-ink-soft mt-1.5 block">
                Formatting: <code className="mono">- item</code> for bullets · <code className="mono">1. item</code> for numbers · blank line for a new paragraph · <code className="mono">**bold**</code> · <code className="mono">*italic*</code>
              </span>
            </label>
            <div className="grid grid-cols-1 gap-4 border-t border-line pt-4">
              <L label="Care instructions (shown in the Care accordion on the product page)">
                <Textarea rows={2} value={form.care} onChange={set("care")} data-testid="pf-care" placeholder="e.g. Dry clean only. Store in a muslin cloth away from direct sunlight." />
              </L>
              <L label="Delivery & returns (shown in the Delivery accordion on the product page)">
                <Textarea rows={2} value={form.delivery} onChange={set("delivery")} data-testid="pf-delivery" placeholder="e.g. Ships in 2-3 weeks (made to order). Complimentary insured delivery." />
              </L>
            </div>
            <SizesField sizes={form.sizes} onChange={(sizes) => setForm({ ...form, sizes })} />

            <div className="grid grid-cols-3 gap-3 border-t border-line pt-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="pf-is-new-label">
                <input type="checkbox" checked={!!form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} data-testid="pf-is-new" />
                <span>Show in <b>Just In</b></span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.is_bestseller} onChange={(e) => setForm({ ...form, is_bestseller: e.target.checked })} data-testid="pf-is-bestseller" />
                <span>Show in <b>Best Sellers</b></span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!form.on_sale} onChange={(e) => setForm({ ...form, on_sale: e.target.checked })} data-testid="pf-on-sale" />
                <span>Show <b>Sale</b> badge</span>
              </label>
            </div>

            <div>
              <p className="overline text-ink-soft mb-2">Occasions · pick all that apply (in addition to the primary occasion above)</p>
              <div className="flex flex-wrap gap-2">
                {OCCS.map((o) => {
                  const selected = (form.occasions || []).includes(o);
                  return (
                    <button
                      type="button"
                      key={o}
                      onClick={() => {
                        const list = form.occasions || [];
                        setForm({
                          ...form,
                          occasions: selected ? list.filter((x) => x !== o) : [...list, o],
                        });
                      }}
                      data-testid={`pf-occasion-tag-${o}`}
                      className={`px-3 py-1.5 border text-sm transition-colors ${selected ? "bg-ink text-ivory border-ink" : "border-line hover:border-ink"}`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex items-start gap-3 border-t border-line pt-4 cursor-pointer" data-testid="pf-offline-only-label">
              <input
                type="checkbox"
                checked={!!form.offline_only}
                onChange={(e) => setForm({ ...form, offline_only: e.target.checked })}
                data-testid="pf-offline-only"
                className="mt-1"
              />
              <div>
                <p className="text-sm font-medium">Sold only in offline store · hide from website</p>
                <p className="text-xs text-ink-soft">Product still shows up in the POS product search and Browse modal for billing, but never appears on amraiya.com.</p>
              </div>
            </label>

            <button onClick={save} className="btn-ink w-full py-3.5 overline" data-testid="save-product-btn">{editing ? "Save changes" : "Create product"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function L({ label, children }) {
  return <label className="block"><span className="overline text-ink-soft mb-1.5 block">{label}</span>{children}</label>;
}

function SizesField({ sizes, onChange }) {
  const [custom, setCustom] = useState("");
  const list = Array.isArray(sizes) ? sizes : [];

  const toggle = (s) => {
    if (list.includes(s)) onChange(list.filter((x) => x !== s));
    else onChange([...list, s]);
  };

  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!list.includes(v)) onChange([...list, v]);
    setCustom("");
  };

  const remove = (s) => onChange(list.filter((x) => x !== s));

  return (
    <div data-testid="pf-sizes">
      <span className="overline text-ink-soft mb-1.5 block">Sizes</span>

      {/* selected chips */}
      <div className="flex flex-wrap gap-2 mb-3 min-h-[32px]">
        {list.length === 0 && (
          <span className="text-xs text-ink-soft italic">No sizes selected yet</span>
        )}
        {list.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1.5 bg-ink text-ivory text-xs px-3 py-1.5 tracking-[0.16em] uppercase"
            data-testid={`size-chip-${s}`}
          >
            {s}
            <button
              type="button"
              onClick={() => remove(s)}
              className="opacity-70 hover:opacity-100"
              aria-label={`Remove ${s}`}
              data-testid={`remove-size-${s}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>

      {/* preset quick-add */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {SIZE_PRESETS.map((s) => {
          const active = list.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggle(s)}
              className={`text-[0.7rem] tracking-[0.18em] uppercase border px-3 py-1.5 transition-colors ${
                active
                  ? "bg-ink text-ivory border-ink"
                  : "bg-white text-ink border-line hover:border-ink"
              }`}
              data-testid={`preset-size-${s}`}
            >
              {s}
            </button>
          );
        })}
      </div>

      {/* custom add */}
      <div className="flex gap-2">
        <Input
          placeholder="Custom size (e.g. 32, EU 38)"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          data-testid="custom-size-input"
        />
        <button
          type="button"
          onClick={addCustom}
          className="overline px-4 border border-line hover:border-ink"
          data-testid="add-custom-size"
        >
          Add
        </button>
      </div>
    </div>
  );
}
